/**
 * Spec 026 + 027 — central wizard step / total mapping.
 *
 * Every onboarding page calls `getStepInfo(stepId, pathKind)` to get the
 * `(current, total)` pair for its progress indicator. This way the bar grows
 * monotonically across the whole flow, even when the user crosses spec
 * boundaries (025 Welcome → 026 ProfileSetup → 027 TeamFlow).
 *
 * Two paths today:
 *
 * - **no-code** (`/welcome` → EntryChoice → register): 4 visible steps.
 *   - Welcome (1), EntryChoice (2), ProfileSetup/OrgPicker (3),
 *     Dashboard (4 — implicit, no bar there).
 *
 * - **with-code** (`/join/:code` → EntryChoice → ProfileSetup → TeamFlow → run): 7 steps.
 *   - 1 Welcome
 *   - 2 EntryChoice (auth-choice — Create new account vs Login)
 *   - 3 ProfileSetup
 *   - 4 FindTeam
 *   - 5 Join *or* Invite (mutually exclusive)
 *   - 6 PickTeamName *or* TeamNamePending *or* TeamWaiting (wait-screens
 *     freeze progress here until they auto-advance)
 *   - 7 TeamReady
 *
 * The legacy GuestJoin step IDs are intentionally retained (mapped to step 6)
 * for any in-flight code path that hasn't been migrated yet; spec 027 deletes
 * the legacy page entirely once 027 ships, so they will be dead aliases.
 */

import type { WizardPathKind } from './WizardStateContext';

export type WizardStepId =
  | 'welcome'
  | 'entry-choice'
  | 'org-picker'
  | 'profile-setup'
  // Spec 027 team-flow step IDs.
  | 'team-find'
  | 'team-join'
  | 'team-pick-name'
  | 'team-name-pending'
  | 'team-waiting'
  | 'team-invite'
  | 'team-ready'
  // Legacy GuestJoin sub-steps (deleted by 027 T019).
  | 'legacy-code-name'
  | 'legacy-choose-mode'
  | 'legacy-create-or-join'
  | 'legacy-success';

const NO_CODE_TOTAL = 4;
const WITH_CODE_TOTAL = 7;

const NO_CODE_STEP_NUMBER: Record<WizardStepId, number> = {
  'welcome': 1,
  'entry-choice': 2,
  'org-picker': 3,
  'profile-setup': 3,
  'team-find': 3,
  'team-join': 3,
  'team-pick-name': 3,
  'team-name-pending': 3,
  'team-waiting': 3,
  'team-invite': 3,
  'team-ready': 3,
  'legacy-code-name': 3,
  'legacy-choose-mode': 3,
  'legacy-create-or-join': 3,
  'legacy-success': 3,
};

/**
 * Spec 027 — `with-code` mapping (corrected per design's onboarding-flow
 * canonical order: Welcome → Setup → Create/Join Team → Pick Team Name).
 *
 * Creator path:
 *   1 Welcome → 2 EntryChoice → 3 Profile → 4 Find → 5 Invite (members join
 *   here, QR + code) → 6 PickTeamName (member list + name + Start Rallye)
 *   → 7 Ready
 *
 * Joiner path:
 *   1 Welcome → 2 EntryChoice → 3 Profile → 4 Find → 5 Join
 *   → 6 Pending|Waiting → 7 Ready
 *
 * Wait-screens (Pending, Waiting) sit at step 5 and don't auto-advance the
 * progress bar; they update in place when WS fires `team.name_updated`.
 */
const WITH_CODE_STEP_NUMBER: Record<WizardStepId, number> = {
  'welcome': 1,
  'entry-choice': 2,
  'org-picker': 3,
  'profile-setup': 3,
  'team-find': 4,
  'team-invite': 5,
  'team-join': 5,
  'team-pick-name': 6,
  'team-name-pending': 6,
  'team-waiting': 6,
  'team-ready': 7,
  'legacy-code-name': 4,
  'legacy-choose-mode': 5,
  'legacy-create-or-join': 6,
  'legacy-success': 7,
};

export interface WizardStepInfo {
  current: number;
  total: number;
}

export function getStepInfo(
  stepId: WizardStepId,
  pathKind: WizardPathKind,
): WizardStepInfo {
  if (pathKind === 'with-code') {
    return { current: WITH_CODE_STEP_NUMBER[stepId], total: WITH_CODE_TOTAL };
  }
  return { current: NO_CODE_STEP_NUMBER[stepId], total: NO_CODE_TOTAL };
}
